import { useLogoutUserMutation, userApi } from '@/features/user/userApi';
import Icon from '@/shared/components/Icon/Icon';
import { IonButton, IonContent, IonItem, IonLabel, IonList, useIonPopover } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import env from '@beam-australia/react-env';
import { balancesApi } from '@/features/balance/balancesApi';
import { setOAuth } from '@/features/oAuth/oAuthSlice';
import { ordersApi } from '@/features/orders/ordersApi';
import { tradesApi } from '@/features/trades/tradesApi';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import ToggleInput from '../ToggleInput/ToggleInput';
import styles from './UserProfileMenu.module.css';
import { useTheme } from '@/shared/hooks/theme';

const PopOver: React.FC<{
  onClickProfil: () => void;
  onClickLogout: () => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
}> = ({ onClickProfil, onClickLogout, isDarkMode, toggleTheme }) => {
  const { t } = useTranslation();

  /* TODO why does it need to be so complicated to align the icons? */
  return (
    <IonContent>
      <IonList lines="none">
        <IonItem
          button={true}
          detail={false}
          className={styles.listItem}
          routerLink="/settings"
          onClick={onClickProfil}
        >
          <div className={styles.iconWrapper}>
            <Icon size="large" src="assets/icons/user.svg" className={styles.icon} />
          </div>
          <IonLabel className={styles.label}>{t('usermenu.profile_settings')}</IonLabel>
        </IonItem>
        <IonItem button={false} detail={false} className={styles.listItem}>
          <div className={styles.iconWrapper}>
            <Icon size="large" src="assets/icons/darkmode.svg" className={`${styles.icon} ${styles.darkMode}`} />
          </div>
          <ToggleInput
            checked={isDarkMode}
            labelPlacement="start"
            name="darkMode"
            toggleHandler={toggleTheme}
            labelText={t('usermenu.dark_mode')}
            labelSize="large"
          />
        </IonItem>
        <IonItem
          button={true}
          detail={false}
          className={styles.listItem}
          onClick={onClickLogout}
          data-testid="userLogoutButton"
        >
          <div className={styles.iconWrapper}>
            <Icon
              size="large"
              src="assets/icons/lock-closed.svg"
              color="danger"
              className={`${styles.icon} ${styles.logOut}`}
            />
          </div>
          <IonLabel className={styles.label}>{t('usermenu.logout')}</IonLabel>
        </IonItem>
      </IonList>
    </IonContent>
  );
};

const UserProfileMenu: React.FC = () => {
  const [userLogout] = useLogoutUserMutation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { isDarkMode, toggleTheme } = useTheme();

  const [present, dismiss] = useIonPopover(PopOver, {
    onClickProfil: (evt: MouseEvent) => {
      dismiss();
      evt.preventDefault();
      history.push('/settings/personal');
    },
    onClickLogout: async () => {
      dismiss();
      try {
        await userLogout();
        // Reset all sensitive api states
        dispatch(setOAuth({ accessToken: null, refreshToken: null }));
        dispatch(userApi.util.resetApiState());
        dispatch(ordersApi.util.resetApiState());
        dispatch(tradesApi.util.resetApiState());
        dispatch(balancesApi.util.resetApiState());

        // we can't use history.push('/') because we want to get a refresh of the page
        // otherwise some navigation doesn't work anymore (it seems due to the AuthorizationGuard)
        window.location.replace(env('HOSTNAME'));
      } catch (error) {
        console.log('handleUserLogout', { error });
      }
    },
    isDarkMode: isDarkMode,
    toggleTheme: toggleTheme
  });

  return (
    <IonButton
      mode="ios"
      className={styles.menuButton}
      fill="clear"
      data-testid="toggleUserMenuButton"
      onClick={(e: any) =>
        present({
          event: e
        })
      }
    >
      <Icon size="xlLarge" src="assets/icons/profile.svg" slot="icon-only" />
    </IonButton>
  );
};

export default UserProfileMenu;
